import styles from './linkedProductsFormModal.module.css'
import { Badge, Data, FieldType, FieldValue, Form, FormField, Modal, ModalId, NumberField, useUIActions, useUIState } from '../../../../../../features/ui'
import { useEffect, useMemo, useState } from 'react'
import { ProductSelectField } from '../../product-select-field/productSelectField'
import { Product, ProductType } from '../../../../state/types'
import { O } from '../../../../../../features/ui'
import { TableName } from '../../../../../../tables'
import { UnitType, unitTypeHelpers, useUnitTypeLoaders } from '../../../../../stock'
import { useProductLoaders } from '../../../../loaders/productLoaders'
import { linkedProductsKey } from '../linked-products-panel/utils'
import { useProductHooks } from '../../../../hooks/productHooks'
import { round } from '../../../../../../utils/typeUtils'
import { useDataTypesHooks } from '../../../../../../features/data-types'

type Props = {
    linkedProducts?: Data
    productsList: Product[]
    onSubmit: (data: Data) => void
}

export const LinkedProductsFormModal = (props: Props) => {
    const { linkedProducts, productsList, onSubmit } = props
    const createMode = !linkedProducts

    const defaultProductId_1 = linkedProducts?.productId_1 as O<string>
    const defaultProductId_2 = linkedProducts?.productId_2 as O<string>
    const defaultEquivalence = linkedProducts?.equivalence as O<number> || 1
    const [productId_1, setProductId_1] = useState<O<string>>(defaultProductId_1)
    const [productId_2, setProductId_2] = useState<O<string>>(defaultProductId_2)
    const [unitType_1, setUnitType_1] = useState<O<UnitType>>()
    const [unitType_2, setUnitType_2] = useState<O<UnitType>>()
    const [equivalence, setEquivalence] = useState<O<number>>(defaultEquivalence)
    const [key, setKey] = useState(0)

    const { openedModal } = useUIState()

    const { getEquivalence } = unitTypeHelpers
    const { describeUnitType } = useProductHooks()
    const { formatNumber } = useDataTypesHooks()

    const productLoaders = useProductLoaders()
    const unitTypeLoaders = useUnitTypeLoaders()

    const { closeModal } = useUIActions()
    
    useEffect(() => setKey(key + 1), [openedModal])
    
    useEffect(() => setProductId_1(defaultProductId_1), [defaultProductId_1])
    useEffect(() => setProductId_2(defaultProductId_2), [defaultProductId_2])
    useEffect(() => setEquivalence(defaultEquivalence), [defaultEquivalence])

    const activeProducts = useMemo(() => productsList.filter(product => [defaultProductId_1, defaultProductId_2].includes(product.id) || !product.archived), [productsList, defaultProductId_1, defaultProductId_2])

    const findProduct = (id: string) => activeProducts.find(product => product.id === id)

    const loadUnitType = async (productId: string) => {
        const { loadProduct } = productLoaders()
        const product = await loadProduct(productId)
        const { findUnitType } = unitTypeLoaders()
        return findUnitType(product!.unitTypeId)!   
    }

    useEffect(() => {
        if (productId_1) {
            loadUnitType(productId_1).then(setUnitType_1)
        } else {
            setUnitType_1(undefined)
        }
    }, [productId_1])

    useEffect(() => {
        if (productId_2) {
            loadUnitType(productId_2).then(setUnitType_2)
        } else {
            setUnitType_2(undefined)
        }
    }, [productId_2])

    useEffect(() => {
        if (unitType_1 && unitType_2 && unitType_1.category === unitType_2.category) {
            const newEquivalence = getEquivalence(unitType_1, unitType_2) || 1
            setEquivalence(newEquivalence)
        }
    }, [unitType_1, unitType_2])

    const onChangeEquivalence = (value?: FieldValue) => setEquivalence(value as O<number>)

    const fields: FormField[] = [
        {
            name: 'productId_1',
            type: FieldType.TABLE,
            table: TableName.PRODUCTS,
            label: 'Producto Principal',
            render: () => <ProductSelectField
                productList={activeProducts}
                defaultValue={defaultProductId_1}
                showStock={false}
                onChange={setProductId_1}
            />
        },
        {
            name: 'productId_2',
            type: FieldType.TABLE,
            table: TableName.PRODUCTS,
            label: 'Producto Asociado',
            render: () => <ProductSelectField
                productList={activeProducts}
                defaultValue={defaultProductId_2}
                showStock={false}
                onChange={setProductId_2}
            />
        }
    ]
    if (unitType_1 && unitType_2) {
        const product_1 = findProduct(productId_1!)
        const product_2 = findProduct(productId_2!)
        const equivalenceField = {
            name: 'equivalence',
            type: FieldType.NUMBER,
            label: 'Equivalencia',
            render: () => (
                <div className={styles.equivalenceContainer}>
                    <span>1 {unitType_1.name.toLowerCase()} = </span>
                    <NumberField
                        id="equivalence"
                        value={equivalence}
                        disabled={unitType_1.category === unitType_2.category}
                        className={styles.equivalence}
                        onChange={onChangeEquivalence}
                    />
                    <span>{unitType_2.abbreviation}</span>
                </div>
            )
        }
        fields.push(equivalenceField)
        if (product_1 && product_2 && equivalence) {
            const infoField = {
                name: 'info',
                type: FieldType.TEXT,
                label: ' ',
                render: () => {
                    const measureUnits_1 = product_1.type === ProductType.BULK ? 1 : product_1.units!
                    const unitTypeLabel_1 = describeUnitType(product_1)
                    const unitTypeAbbr_1 = unitType_1.abbreviation
                    const measureUnits_2 = round(measureUnits_1 * equivalence)
                    const units_2 = round(measureUnits_2 / (product_2.units || 1))
                    const unitTypeLabel_2 = describeUnitType(product_2, true, units_2 > 1)
                    const unitTypeAbbr_2 = unitType_2.abbreviation
                    return (
                        <div className={styles.info}>
                            {unitTypeLabel_1 === unitTypeAbbr_1 && measureUnits_1 === 1 ? (
                                <span>1 {unitTypeLabel_1}</span>
                            ) : (
                                <>
                                    <span>1 {unitTypeLabel_1}</span>
                                    <Badge label={`(${formatNumber(measureUnits_1)} ${unitType_1.abbreviation})`} />
                                </>
                            )}
                            <span className={styles.equals}>=</span>
                            {unitTypeLabel_2 === unitTypeAbbr_2 && units_2 === measureUnits_2 ? (
                                <Badge label={`${formatNumber(measureUnits_2)} ${unitTypeAbbr_2}`} />    
                            ) : (
                                <>
                                    <span>{formatNumber(units_2)} {unitTypeLabel_2}</span>
                                    <Badge label={`(${formatNumber(measureUnits_2)} ${unitTypeAbbr_2})`} />
                                </>
                            )}
                        </div>
                    )
                }
            }
            fields.push(infoField)
        }
    }

    const handleSubmit = () => {
        const linkedProductData = {
            key: linkedProductsKey(productId_1!, productId_2!),
            productId_1,
            productId_2,
            unitTypeId_1: unitType_1?.id,
            unitTypeId_2: unitType_2?.id,
            equivalence
        }
        onSubmit(linkedProductData)
        closeModal(ModalId.LINKED_PRODUCTS_FORM)
    }

    const handleCancel = () => closeModal(ModalId.LINKED_PRODUCTS_FORM)

    return (
        <Modal
            modalId={ModalId.LINKED_PRODUCTS_FORM}
            title="Asociar Productos"
            actions={[]}
            widthSize='m'
        >
            <Form
                key={key}
                formId="linked-products-form"
                fields={fields}
                createMode={createMode}
                createLabel="Asociar"
                onSubmit={handleSubmit}
                onCancel={handleCancel}
            />
        </Modal>
    )
}
